<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AceleresTeamPresenceAPI from 'dashboard/api/aceleresTeamPresence';
import { frontendURL } from 'dashboard/helper/URLHelper';

const BRAZIL_TIME_ZONE = 'America/Sao_Paulo';

const route = useRoute();
const router = useRouter();
const accountId = route.params.accountId;

const formatDateInput = value => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: BRAZIL_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(value);
  const year = parts.find(part => part.type === 'year')?.value;
  const month = parts.find(part => part.type === 'month')?.value;
  const day = parts.find(part => part.type === 'day')?.value;
  return `${year}-${month}-${day}`;
};

const getTodayInput = () => formatDateInput(new Date());

const formatLongDate = value =>
  new Intl.DateTimeFormat('pt-BR', {
    timeZone: BRAZIL_TIME_ZONE,
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(new Date(`${value}T12:00:00-03:00`));

const loading = ref(true);
const hasError = ref(false);
const fromDate = ref(getTodayInput());
const toDate = ref(getTodayInput());
const historyDays = ref([]);
const expandedDay = ref(null);
const lastUpdated = ref(null);
const periodMenuOpen = ref(false);
const periodMenuRef = ref(null);

const periodOptions = [
  { label: 'Hoje', days: 1 },
  { label: 'Últimos 7 dias', days: 7 },
  { label: 'Últimos 14 dias', days: 14 },
  { label: 'Últimos 30 dias', days: 30 },
];

const fetchHistory = async () => {
  if (!fromDate.value) {
    fromDate.value = getTodayInput();
  }

  if (!toDate.value) {
    toDate.value = fromDate.value;
  }

  if (fromDate.value > toDate.value) {
    const temp = fromDate.value;
    fromDate.value = toDate.value;
    toDate.value = temp;
  }

  loading.value = true;
  try {
    const { data } = await AceleresTeamPresenceAPI.getHistory({
      from: fromDate.value,
      to: toDate.value,
    });
    historyDays.value = data.days || [];
    expandedDay.value = historyDays.value[0]?.date || null;
    lastUpdated.value = data.generated_at
      ? new Date(data.generated_at)
      : new Date();
    hasError.value = false;
  } catch (error) {
    hasError.value = true;
  } finally {
    loading.value = false;
  }
};

const goBack = () => {
  router.push(frontendURL(`accounts/${accountId}/equipe-online`));
};

const setToday = async () => {
  const today = getTodayInput();
  fromDate.value = today;
  toDate.value = today;
  await fetchHistory();
};

const setLast7Days = async () => {
  await setPeriodDays(7);
};

const setPeriodDays = async days => {
  const end = new Date(`${getTodayInput()}T12:00:00-03:00`);
  const start = new Date(end);
  start.setDate(end.getDate() - (days - 1));

  fromDate.value = formatDateInput(start);
  toDate.value = formatDateInput(end);
  periodMenuOpen.value = false;
  await fetchHistory();
};

const togglePeriodMenu = () => {
  periodMenuOpen.value = !periodMenuOpen.value;
};

const selectPeriodOption = async option => {
  await setPeriodDays(option.days);
};

const handleDocumentClick = event => {
  if (!periodMenuRef.value) return;
  if (!periodMenuRef.value.contains(event.target)) {
    periodMenuOpen.value = false;
  }
};

const toggleDay = date => {
  expandedDay.value = expandedDay.value === date ? null : date;
};

const formatResponse = value => {
  if (value === null || value === undefined) return '—';
  if (value < 60) return `${value} min`;
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  return minutes ? `${hours}h ${minutes}min` : `${hours}h`;
};

const formatLeadTime = epoch => {
  if (!epoch) return '—';
  return new Date(epoch * 1000).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDurationSeconds = value => {
  const seconds = Number(value) || 0;
  if (seconds <= 0) return '0min';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours && minutes) return `${hours}h ${minutes}min`;
  if (hours) return `${hours}h`;
  return `${Math.max(1, minutes)}min`;
};

const presenceTotal = (day, status) => day.summary?.presence?.[status] || 0;
const presenceSellers = day =>
  (day.presence_history || []).filter(seller => seller.total_seconds > 0);

const overallSummary = computed(() =>
  historyDays.value.reduce(
    (acc, day) => {
      acc.received += day.summary?.received_today || 0;
      acc.answered += day.summary?.answered_today || 0;
      acc.waiting += day.summary?.waiting_today || 0;
      acc.aurora += day.summary?.aurora_today || 0;
      acc.missed += day.summary?.missed_today || 0;
      const avg = day.summary?.avg_response_minutes;
      if (avg !== null && avg !== undefined) {
        acc.avgSamples += 1;
        acc.avgTotal += avg;
      }
      return acc;
    },
    {
      received: 0,
      answered: 0,
      waiting: 0,
      aurora: 0,
      missed: 0,
      avgSamples: 0,
      avgTotal: 0,
    }
  )
);

const averageResponse = computed(() => {
  if (!overallSummary.value.avgSamples) return '—';
  return formatResponse(
    Math.round(overallSummary.value.avgTotal / overallSummary.value.avgSamples)
  );
});

const periodLabel = computed(() => {
  if (fromDate.value === toDate.value) {
    return fromDate.value === getTodayInput()
      ? 'Hoje'
      : formatLongDate(fromDate.value);
  }

  return `${formatLongDate(fromDate.value)} até ${formatLongDate(toDate.value)}`;
});

const dayCountLabel = computed(() =>
  historyDays.value.length === 1 ? '1 dia' : `${historyDays.value.length} dias`
);

const selectedRangeDays = computed(() => {
  if (!fromDate.value || !toDate.value) return null;

  const start = new Date(`${fromDate.value}T12:00:00-03:00`);
  const end = new Date(`${toDate.value}T12:00:00-03:00`);
  const diff = Math.round((end - start) / 86400000) + 1;
  return Number.isFinite(diff) && diff > 0 ? diff : null;
});

const periodTriggerLabel = computed(() => {
  const preset = periodOptions.find(
    option => option.days === selectedRangeDays.value
  );
  if (preset) return preset.label;
  return 'Período personalizado';
});

const dayCardClass = computed(() =>
  historyDays.value.length === 1
    ? 'md:col-span-2 xl:col-span-2 2xl:col-span-3'
    : 'md:col-span-1'
);

onMounted(fetchHistory);
onMounted(() => document.addEventListener('click', handleDocumentClick));
onBeforeUnmount(() =>
  document.removeEventListener('click', handleDocumentClick)
);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <header
      class="flex items-center justify-between flex-shrink-0 gap-4 px-6 py-4"
    >
      <div class="flex items-center min-w-0 gap-3">
        <button
          type="button"
          class="inline-flex items-center justify-center flex-shrink-0 size-10 rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-12 hover:bg-n-alpha-2 shadow-sm transition-colors"
          aria-label="Voltar para equipe online"
          title="Voltar para equipe online"
          @click="goBack"
        >
          <span class="i-lucide-arrow-left size-4" />
        </button>
        <div class="min-w-0">
          <h1 class="text-[28px] font-bold truncate text-n-slate-12">
            Histórico da equipe
          </h1>
          <p class="mt-1 text-base text-n-slate-11">
            Histórico diário de atendimentos, respostas e volume recebido
          </p>
        </div>
      </div>

      <div
        class="acl-mobile-account-menu-host flex flex-wrap items-center justify-end gap-3"
      >
        <div class="flex flex-col gap-2">
          <h2
            class="text-sm font-semibold tracking-wide uppercase text-n-slate-12"
          >
            Filtros
          </h2>
          <div
            class="flex flex-wrap items-end gap-2 rounded-2xl border border-n-weak bg-n-surface-1 px-4 py-3 shadow-sm"
          >
            <div ref="periodMenuRef" class="relative">
              <button
                type="button"
                class="inline-flex h-10 min-w-[190px] items-center justify-between gap-2 rounded-lg border border-[#d4d7dd] bg-transparent px-3 text-sm font-medium text-n-slate-12 shadow-sm transition-colors hover:border-[#b7bec8]"
                @click.stop="togglePeriodMenu"
              >
                <span>{{ periodTriggerLabel }}</span>
                <span
                  :class="
                    periodMenuOpen
                      ? 'i-lucide-chevron-up'
                      : 'i-lucide-chevron-down'
                  "
                  class="size-4"
                />
              </button>

              <div
                v-if="periodMenuOpen"
                class="absolute left-0 top-[calc(100%+0.5rem)] z-50 w-56 overflow-hidden rounded-xl border border-n-weak bg-n-surface-1 shadow-xl"
              >
                <button
                  v-for="option in periodOptions"
                  :key="option.days"
                  type="button"
                  class="flex w-full items-center justify-between px-4 py-3 text-sm text-n-slate-12 transition-colors hover:bg-n-alpha-2"
                  @click="selectPeriodOption(option)"
                >
                  <span>{{ option.label }}</span>
                  <span class="text-xs text-n-slate-10"
                    >{{ option.days }}d</span
                  >
                </button>
              </div>
            </div>

            <div class="flex flex-col gap-1">
              <label
                for="history-from"
                class="text-xs font-semibold uppercase tracking-wide text-n-slate-11"
              >
                De
              </label>
              <input
                id="history-from"
                v-model="fromDate"
                type="date"
                class="h-10 min-w-[150px] rounded-lg border border-[#d4d7dd] bg-transparent px-3 text-sm text-n-slate-12 outline-none transition-colors focus:border-[#94a3b8]"
                @change="fetchHistory"
              />
            </div>

            <div class="flex flex-col gap-1">
              <label
                for="history-to"
                class="text-xs font-semibold uppercase tracking-wide text-n-slate-11"
              >
                Até
              </label>
              <input
                id="history-to"
                v-model="toDate"
                type="date"
                class="h-10 min-w-[150px] rounded-lg border border-[#d4d7dd] bg-transparent px-3 text-sm text-n-slate-12 outline-none transition-colors focus:border-[#94a3b8]"
                @change="fetchHistory"
              />
            </div>
          </div>
        </div>

        <span v-if="lastUpdated" class="text-sm font-medium text-n-slate-12">
          Atualizado às
          {{
            lastUpdated.toLocaleTimeString('pt-BR', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })
          }}
        </span>

        <button
          type="button"
          class="inline-flex items-center gap-2 h-10 px-4 text-sm font-semibold rounded-lg border border-0 bg-[#059669] text-white hover:bg-[#047857] shadow-sm transition-colors"
          @click="fetchHistory"
        >
          <span class="i-lucide-refresh-cw size-4" />
          Atualizar
        </button>
      </div>
    </header>

    <div class="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
      <div
        class="grid flex-shrink-0 gap-4 px-6 py-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-5"
      >
        <div
          class="flex items-center gap-4 w-full px-5 py-5 text-left transition-all border rounded-2xl border-n-weak bg-n-surface-1 min-w-0 min-h-[92px] shadow-md shadow-black/5"
        >
          <span
            class="inline-flex items-center justify-center flex-shrink-0 rounded-full size-11 shadow-sm bg-[#4f46e514] text-[#4f46e5]"
          >
            <span class="i-lucide-calendar-range size-5" />
          </span>
          <div class="flex flex-col min-w-0">
            <span class="text-2xl font-semibold leading-none text-n-slate-12">{{
              dayCountLabel
            }}</span>
            <span class="text-base text-n-slate-11 truncate">{{
              periodLabel
            }}</span>
          </div>
        </div>

        <div
          class="flex items-center gap-4 w-full px-5 py-5 text-left transition-all border rounded-2xl border-n-weak bg-n-surface-1 min-w-0 min-h-[92px] shadow-md shadow-black/5"
        >
          <span
            class="inline-flex items-center justify-center flex-shrink-0 rounded-full size-11 shadow-sm bg-[#4f46e514] text-[#4f46e5]"
          >
            <span class="i-lucide-inbox size-5" />
          </span>
          <div class="flex flex-col min-w-0">
            <span class="text-2xl font-semibold leading-none text-n-slate-12">{{
              overallSummary.received
            }}</span>
            <span class="text-base text-n-slate-11 truncate">Recebidos</span>
          </div>
        </div>

        <div
          class="flex items-center gap-4 w-full px-5 py-5 text-left transition-all border rounded-2xl border-n-weak bg-n-surface-1 min-w-0 min-h-[92px] shadow-md shadow-black/5"
        >
          <span
            class="inline-flex items-center justify-center flex-shrink-0 rounded-full size-11 shadow-sm bg-[#10b98114] text-[#10b981]"
          >
            <span class="i-lucide-message-circle-more size-5" />
          </span>
          <div class="flex flex-col min-w-0">
            <span class="text-2xl font-semibold leading-none text-n-slate-12">{{
              overallSummary.answered
            }}</span>
            <span class="text-base text-n-slate-11 truncate">Atendidos</span>
          </div>
        </div>

        <div
          class="flex items-center gap-4 w-full px-5 py-5 text-left transition-all border rounded-2xl border-n-weak bg-n-surface-1 min-w-0 min-h-[92px] shadow-md shadow-black/5"
        >
          <span
            class="inline-flex items-center justify-center flex-shrink-0 rounded-full size-11 shadow-sm bg-[#dc262614] text-[#dc2626]"
          >
            <span class="i-lucide-clock-3 size-5" />
          </span>
          <div class="flex flex-col min-w-0">
            <span class="text-2xl font-semibold leading-none text-n-slate-12">{{
              overallSummary.waiting
            }}</span>
            <span class="text-base text-n-slate-11 truncate">Aguardando</span>
          </div>
        </div>

        <div
          class="flex items-center gap-4 w-full px-5 py-5 text-left transition-all border rounded-2xl border-n-weak bg-n-surface-1 min-w-0 min-h-[92px] shadow-md shadow-black/5"
        >
          <span
            class="inline-flex items-center justify-center flex-shrink-0 rounded-full size-11 shadow-sm bg-[#a855f714] text-[#a855f7]"
          >
            <span class="i-lucide-sparkles size-5" />
          </span>
          <div class="flex flex-col min-w-0">
            <span class="text-2xl font-semibold leading-none text-n-slate-12">{{
              averageResponse
            }}</span>
            <span class="text-base text-n-slate-11 truncate"
              >Média 1ª resposta</span
            >
          </div>
        </div>
      </div>

      <div class="px-6 pb-8">
        <div
          class="flex items-center justify-between gap-3 pt-4 mt-2 mb-4 border-t border-n-weak"
        >
          <div>
            <h2
              class="text-base font-semibold tracking-wide uppercase text-n-slate-12"
            >
              Dias do período
            </h2>
            <p class="mt-1 text-sm text-n-slate-11">
              Resultado consolidado de {{ periodLabel }}
            </p>
          </div>
          <span
            class="inline-flex items-center gap-2 px-3 py-1 text-sm font-semibold rounded-full bg-n-alpha-1 text-n-slate-12"
          >
            {{ historyDays.length }} registros
          </span>
        </div>

        <div
          v-if="loading && !historyDays.length"
          class="py-16 text-sm text-center text-n-slate-10"
        >
          Carregando histórico…
        </div>

        <div
          v-else-if="hasError"
          class="py-16 text-sm text-center text-n-ruby-11"
        >
          Não foi possível carregar o histórico. Tente novamente.
        </div>

        <div
          v-else-if="!historyDays.length"
          class="py-16 text-sm text-center text-n-slate-10"
        >
          Nenhum dado de histórico disponível.
        </div>

        <div v-else class="grid gap-4 xl:grid-cols-2 2xl:grid-cols-3">
          <article
            v-for="day in historyDays"
            :key="day.date"
            :class="[
              'rounded-2xl border border-n-weak bg-n-surface-1 p-5 shadow-sm shadow-black/5',
              dayCardClass,
            ]"
          >
            <div class="flex flex-wrap items-start justify-between gap-4">
              <div class="min-w-0">
                <h3 class="text-xl font-semibold text-n-slate-12">
                  {{ day.weekday }}
                </h3>
                <p class="mt-1 text-sm text-n-slate-11">
                  {{ day.label }}
                </p>
              </div>

              <div class="flex items-center gap-2">
                <span
                  class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-n-alpha-1 text-sm font-semibold text-n-slate-12"
                >
                  ⏱ {{ formatResponse(day.summary?.avg_response_minutes) }}
                </span>
                <button
                  type="button"
                  class="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-semibold rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
                  @click="toggleDay(day.date)"
                >
                  <span>{{
                    expandedDay === day.date
                      ? 'Ocultar detalhes'
                      : 'Ver detalhes'
                  }}</span>
                  <span
                    :class="
                      expandedDay === day.date
                        ? 'i-lucide-chevron-up'
                        : 'i-lucide-chevron-down'
                    "
                    class="size-4"
                  />
                </button>
              </div>
            </div>

            <div
              class="grid grid-cols-2 gap-3 mt-4 sm:grid-cols-3 xl:grid-cols-5"
            >
              <div class="p-4 rounded-xl bg-n-alpha-1">
                <div class="text-xs uppercase text-n-slate-10">Recebidos</div>
                <div class="mt-1 text-2xl font-semibold text-n-slate-12">
                  {{ day.summary?.received_today || 0 }}
                </div>
              </div>
              <div class="p-4 rounded-xl bg-n-alpha-1">
                <div class="text-xs uppercase text-n-slate-10">Atendidos</div>
                <div class="mt-1 text-2xl font-semibold text-n-slate-12">
                  {{ day.summary?.answered_today || 0 }}
                </div>
              </div>
              <div class="p-4 rounded-xl bg-n-alpha-1">
                <div class="text-xs uppercase text-n-slate-10">Aguardando</div>
                <div class="mt-1 text-2xl font-semibold text-n-slate-12">
                  {{ day.summary?.waiting_today || 0 }}
                </div>
              </div>
              <div class="p-4 rounded-xl bg-n-alpha-1">
                <div class="text-xs uppercase text-n-slate-10">Aurora</div>
                <div class="mt-1 text-2xl font-semibold text-n-slate-12">
                  {{ day.summary?.aurora_today || 0 }}
                </div>
              </div>
              <div class="p-4 rounded-xl bg-n-alpha-1">
                <div class="text-xs uppercase text-n-slate-10">Vencidos</div>
                <div class="mt-1 text-2xl font-semibold text-n-slate-12">
                  {{ day.summary?.missed_today || 0 }}
                </div>
              </div>
            </div>

            <div class="grid grid-cols-3 gap-3 mt-3">
              <div class="p-4 rounded-xl bg-n-alpha-1">
                <div class="text-xs uppercase text-n-slate-10">Online</div>
                <div class="mt-1 text-lg font-semibold text-emerald-600">
                  {{ formatDurationSeconds(presenceTotal(day, 'active')) }}
                </div>
              </div>
              <div class="p-4 rounded-xl bg-n-alpha-1">
                <div class="text-xs uppercase text-n-slate-10">Ocioso</div>
                <div class="mt-1 text-lg font-semibold text-amber-600">
                  {{ formatDurationSeconds(presenceTotal(day, 'idle')) }}
                </div>
              </div>
              <div class="p-4 rounded-xl bg-n-alpha-1">
                <div class="text-xs uppercase text-n-slate-10">Off</div>
                <div class="mt-1 text-lg font-semibold text-n-slate-11">
                  {{ formatDurationSeconds(presenceTotal(day, 'offline')) }}
                </div>
              </div>
            </div>

            <div class="mt-4">
              <div class="flex items-center justify-between gap-3 mb-2">
                <h4
                  class="text-sm font-semibold uppercase tracking-wide text-n-slate-11"
                >
                  Destaques por vendedor
                </h4>
              </div>
              <div class="grid gap-2 md:grid-cols-2 xl:grid-cols-2">
                <div
                  v-for="seller in day.sellers.slice(0, 4)"
                  :key="seller.user_id"
                  class="flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-n-alpha-1"
                >
                  <div class="min-w-0">
                    <div class="text-sm font-semibold truncate text-n-slate-12">
                      {{ seller.name }}
                    </div>
                    <div class="text-xs text-n-slate-10">
                      {{ seller.received_today }} recebidos ·
                      {{ seller.answered_today }} atendidos
                    </div>
                  </div>
                  <div class="text-xs font-semibold text-n-slate-12">
                    {{ seller.missed_today }} vencidos
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="expandedDay === day.date"
              class="pt-4 mt-4 space-y-4 border-t border-n-weak"
            >
              <section class="rounded-2xl bg-n-alpha-1 p-4">
                <div class="flex items-center justify-between gap-3 mb-3">
                  <h5
                    class="text-sm font-semibold uppercase tracking-wide text-n-slate-11"
                  >
                    Histórico de acesso
                  </h5>
                  <span class="text-xs font-semibold text-n-slate-10">
                    {{ presenceSellers(day).length }} vendedores com registro
                  </span>
                </div>

                <div
                  v-if="!presenceSellers(day).length"
                  class="text-sm text-n-slate-10"
                >
                  Nenhum intervalo de acesso registrado neste dia.
                </div>

                <div v-else class="grid gap-3 xl:grid-cols-2">
                  <div
                    v-for="seller in presenceSellers(day)"
                    :key="`presence-${day.date}-${seller.user_id}`"
                    class="rounded-xl bg-n-surface-1 p-4"
                  >
                    <div class="flex items-center justify-between gap-3">
                      <div
                        class="text-sm font-semibold truncate text-n-slate-12"
                      >
                        {{ seller.name }}
                      </div>
                      <div class="text-xs font-semibold text-n-slate-10">
                        Total {{ formatDurationSeconds(seller.total_seconds) }}
                      </div>
                    </div>

                    <div class="mt-3 space-y-3">
                      <div
                        v-for="status in seller.statuses"
                        :key="`presence-${day.date}-${seller.user_id}-${status.status}`"
                      >
                        <div class="flex items-center justify-between gap-3">
                          <span
                            class="text-xs font-semibold uppercase text-n-slate-11"
                          >
                            {{ status.label }}
                          </span>
                          <span class="text-xs font-semibold text-n-slate-12">
                            {{ formatDurationSeconds(status.total_seconds) }}
                          </span>
                        </div>

                        <div
                          v-if="status.intervals.length"
                          class="mt-2 flex flex-wrap gap-2"
                        >
                          <span
                            v-for="interval in status.intervals"
                            :key="`${interval.start_at}-${interval.end_at}`"
                            class="inline-flex items-center gap-2 rounded-lg bg-n-alpha-1 px-2 py-1 text-xs font-medium text-n-slate-12"
                          >
                            {{ interval.start_label }} às
                            {{ interval.end_label }}
                            <span class="text-n-slate-10">
                              {{
                                formatDurationSeconds(interval.duration_seconds)
                              }}
                            </span>
                          </span>
                        </div>
                        <div v-else class="mt-1 text-xs text-n-slate-10">
                          Sem registro.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <div class="grid gap-4 lg:grid-cols-3">
                <section class="rounded-2xl bg-n-alpha-1 p-4">
                  <h5
                    class="mb-2 text-sm font-semibold uppercase tracking-wide text-n-slate-11"
                  >
                    Aguardando resposta
                  </h5>
                  <div
                    v-if="!day.overview?.waiting?.length"
                    class="text-sm text-n-slate-10"
                  >
                    Nenhum lead pendente neste dia.
                  </div>
                  <ul v-else class="space-y-2">
                    <li
                      v-for="lead in day.overview.waiting"
                      :key="`w-${day.date}-${lead.display_id}`"
                      class="p-3 rounded-xl bg-n-surface-1"
                    >
                      <div class="flex items-center justify-between gap-2">
                        <span class="text-sm font-semibold text-n-slate-12">
                          {{ lead.contact_name }} #{{ lead.display_id }}
                        </span>
                        <span class="text-xs text-n-slate-10">
                          {{ formatLeadTime(lead.first_inbound_at) }}
                        </span>
                      </div>
                      <div class="mt-1 text-xs text-n-slate-10">
                        {{ lead.current_assignee || 'Sem responsável atual' }}
                        <span v-if="lead.passed_by && lead.passed_by.length">
                          · passou por {{ lead.passed_by.join(', ') }}
                        </span>
                      </div>
                    </li>
                  </ul>
                </section>

                <section class="rounded-2xl bg-n-alpha-1 p-4">
                  <h5
                    class="mb-2 text-sm font-semibold uppercase tracking-wide text-n-slate-11"
                  >
                    Atendidos no dia
                  </h5>
                  <div
                    v-if="!day.overview?.answered?.length"
                    class="text-sm text-n-slate-10"
                  >
                    Nenhum atendimento humano neste dia.
                  </div>
                  <ul v-else class="space-y-2">
                    <li
                      v-for="lead in day.overview.answered"
                      :key="`a-${day.date}-${lead.display_id}`"
                      class="p-3 rounded-xl bg-n-surface-1"
                    >
                      <div class="flex items-center justify-between gap-2">
                        <span class="text-sm font-semibold text-n-slate-12">
                          {{ lead.contact_name }} #{{ lead.display_id }}
                        </span>
                        <span class="text-xs font-semibold text-emerald-600">
                          {{ formatResponse(lead.response_minutes) }}
                        </span>
                      </div>
                      <div class="mt-1 text-xs text-n-slate-10">
                        por {{ lead.answered_by }}
                      </div>
                    </li>
                  </ul>
                </section>

                <section class="rounded-2xl bg-n-alpha-1 p-4">
                  <h5
                    class="mb-2 text-sm font-semibold uppercase tracking-wide text-n-slate-11"
                  >
                    Atendidos pela Aurora
                  </h5>
                  <div
                    v-if="!day.overview?.aurora?.length"
                    class="text-sm text-n-slate-10"
                  >
                    Nenhum atendimento da Aurora neste dia.
                  </div>
                  <ul v-else class="space-y-2">
                    <li
                      v-for="lead in day.overview.aurora"
                      :key="`u-${day.date}-${lead.display_id}`"
                      class="p-3 rounded-xl bg-n-surface-1"
                    >
                      <div class="flex items-center justify-between gap-2">
                        <span class="text-sm font-semibold text-n-slate-12">
                          {{ lead.contact_name }} #{{ lead.display_id }}
                        </span>
                        <span class="text-xs font-semibold text-[#a855f7]">
                          {{ formatResponse(lead.response_minutes) }}
                        </span>
                      </div>
                      <div class="mt-1 text-xs text-n-slate-10">
                        {{ lead.answered_by }}
                      </div>
                    </li>
                  </ul>
                </section>
              </div>
            </div>
          </article>
        </div>
      </div>
    </div>
  </div>
</template>
